/* Homepage format preview v1. No dependencies or commerce actions. */
(() => {
  if (customElements.get('md-pwyw-preview')) return;

  class MdPwywPreview extends HTMLElement {
    connectedCallback() {
      // Also handles editor re-renders and reordered/reinserted sections.
      this.mountFrame = requestAnimationFrame(() => this.mount());
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.mountFrame);
      this.requestId = (this.requestId || 0) + 1;
      this.abortController?.abort();
      this.abortController = null;
      this.imageObserver?.disconnect();
      this.removeAttribute('aria-busy');
      const controls = this.querySelector('[data-md-preview-controls]');
      if (controls) controls.hidden = true;
    }

    mount() {
      if (!this.isConnected || this.abortController) return;
      this.buttons = [...this.querySelectorAll('[data-md-preview-button]')];
      this.images = [...this.querySelectorAll('[data-md-preview-image]')];
      this.copies = [...this.querySelectorAll('[data-md-preview-copy]')];
      this.controls = this.querySelector('[data-md-preview-controls]');
      this.pills = this.querySelector('[data-md-preview-pills]');
      this.status = this.querySelector('[data-md-preview-status]');
      const formats = ['ebooks', 'audio', 'bundle'];
      if (!this.controls || !this.pills || !this.status ||
          formats.some(format =>
            !this.buttons.some(el => el.dataset.mdPreviewButton === format) ||
            !this.images.some(el => el.dataset.mdPreviewImage === format) ||
            !this.copies.some(el => el.dataset.mdPreviewCopy === format))) return;

      this.abortController = new AbortController();
      const { signal } = this.abortController;
      this.requestId = 0;
      this.buttons.forEach((button, index) => {
        button.addEventListener('click', () => {
          this.select(button.dataset.mdPreviewButton);
        }, { signal });
        button.addEventListener('keydown', event => {
          let next;
          const rtl = getComputedStyle(this.pills).direction === 'rtl';
          if (event.key === 'ArrowRight') next = index + (rtl ? -1 : 1);
          else if (event.key === 'ArrowLeft') next = index + (rtl ? 1 : -1);
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = this.buttons.length - 1;
          else return; // Native Enter/Space generate a click.
          event.preventDefault();
          const target = this.buttons[(next + this.buttons.length) % this.buttons.length];
          target.focus({ preventScroll: true });
          this.select(target.dataset.mdPreviewButton);
        }, { signal });
      });

      // Request alternative art near the viewport, not on a background timer.
      const warmImages = () => {
        this.images.forEach(layer => {
          const image = layer.querySelector('img');
          if (image) image.loading = 'eager';
        });
      };
      if ('IntersectionObserver' in window) {
        this.imageObserver = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) {
            warmImages();
            this.imageObserver.disconnect();
          }
        }, { rootMargin: '300px' });
        this.imageObserver.observe(this);
      } else {
        warmImages();
      }

      this.controls.hidden = false;
      this.select(this.dataset.activeFormat || 'bundle', false);
    }

    waitForImage(image, signal) {
      if (!image || image.complete) {
        return Promise.resolve(!image || image.naturalWidth > 0);
      }
      return new Promise(resolve => {
        let timer;
        let settled = false;
        const finish = success => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          image.removeEventListener('load', onLoad);
          image.removeEventListener('error', onError);
          signal.removeEventListener('abort', onError);
          resolve(success);
        };
        const onLoad = () => finish(image.naturalWidth > 0);
        const onError = () => finish(false);
        image.addEventListener('load', onLoad, { once: true });
        image.addEventListener('error', onError, { once: true });
        signal.addEventListener('abort', onError, { once: true });
        timer = setTimeout(onError, 6000);
        if (image.complete) onLoad();
        else if (signal.aborted) onError();
      });
    }

    async select(format, announce = true) {
      const index = this.buttons.findIndex(el => el.dataset.mdPreviewButton === format);
      if (index < 0 || !this.abortController) return;
      const imageLayer = this.images.find(el => el.dataset.mdPreviewImage === format);
      const image = imageLayer.querySelector('img');
      const token = ++this.requestId;
      const { signal } = this.abortController;
      if (image) image.loading = 'eager';
      this.setAttribute('aria-busy', 'true');
      const success = await this.waitForImage(image, signal);
      // Rapid clicks or a removed editor section must not commit stale results.
      if (signal.aborted || !this.isConnected || token !== this.requestId) return;

      const errorMessage = imageLayer.querySelector('[data-md-preview-image-error]');
      if (image) image.hidden = !success;
      if (errorMessage) errorMessage.hidden = success;
      this.dataset.activeFormat = format;
      const rtl = getComputedStyle(this.pills).direction === 'rtl';
      this.pills.dataset.activeIndex = String(rtl ? this.buttons.length - 1 - index : index);
      this.buttons.forEach(button => {
        const selected = button.dataset.mdPreviewButton === format;
        button.setAttribute('aria-pressed', String(selected));
        button.classList.toggle('is-active', selected);
      });
      this.images.forEach(layer => {
        const selected = layer.dataset.mdPreviewImage === format;
        layer.classList.toggle('is-active', selected);
        layer.setAttribute('aria-hidden', String(!selected));
      });
      this.copies.forEach(copy => {
        const selected = copy.dataset.mdPreviewCopy === format;
        copy.classList.toggle('is-active', selected);
        copy.setAttribute('aria-hidden', String(!selected));
        copy.toggleAttribute('inert', !selected);
      });
      this.removeAttribute('aria-busy');
      if (announce) {
        const label = this.buttons[index].dataset.mdPreviewLabel || format;
        const copy = this.copies.find(el => el.dataset.mdPreviewCopy === format);
        this.status.textContent = `${label}. ${copy.textContent.trim()}`;
      }
    }
  }
  customElements.define('md-pwyw-preview', MdPwywPreview);
})();
